import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { api } from '../../utils/api';
import { toast } from 'sonner';

const SEOEditor = () => {
  const [loading, setLoading] = useState(false);
  const agent = JSON.parse(localStorage.getItem('agent') || '{}');
  const [seo, setSeo] = useState({
    meta_title: '',
    meta_description: '',
    og_title: '',
    og_description: '',
    canonical_url: ''
  });

  useEffect(() => {
    // Load existing SEO settings
    const loadSEO = async () => {
      try {
        const data = await api.getAgentPublic(agent.slug);
        if (data.seo && Object.keys(data.seo).length > 0) {
          setSeo({
            meta_title: data.seo.meta_title || '',
            meta_description: data.seo.meta_description || '',
            og_title: data.seo.og_title || '',
            og_description: data.seo.og_description || '',
            canonical_url: data.seo.canonical_url || ''
          });
        }
      } catch (error) {
        console.error('Failed to load SEO settings:', error);
      }
    };
    if (agent.slug) loadSEO();
  }, [agent.slug]);

  const handleSave = async () => {
    setLoading(true);
    try {
      await api.updateSEO(agent.slug, seo);
      toast.success('Paramètres SEO mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setSeo(prev => ({ ...prev, [field]: value }));
  };

  const getCharCount = (text, max) => {
    const count = text?.length || 0;
    const color = count > max ? 'text-red-600' : count > max * 0.9 ? 'text-orange-600' : 'text-muted-foreground';
    return <span className={`text-xs ${color}`}>{count}/{max}</span>;
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold mb-2">Paramètres SEO</h1>
        <p className="text-muted-foreground">
          Optimisez le référencement de votre page landing
        </p>
      </div>

      {/* Meta Tags */}
      <Card className="p-6 space-y-6">
        <div>
          <h3 className="font-semibold mb-4">Balises Meta</h3>
          
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="meta_title">Titre de la page</Label>
                {getCharCount(seo.meta_title, 60)}
              </div>
              <Input
                id="meta_title"
                value={seo.meta_title}
                onChange={(e) => updateField('meta_title', e.target.value)}
                placeholder={`Estimation Immobilière ${agent.city} | Expert Local`}
                maxLength={70}
                data-testid="meta-title-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Apparait dans les résultats Google. Optimal: 50-60 caractères
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="meta_description">Meta Description</Label>
                {getCharCount(seo.meta_description, 160)}
              </div>
              <Textarea
                id="meta_description"
                value={seo.meta_description}
                onChange={(e) => updateField('meta_description', e.target.value)}
                rows={3}
                maxLength={170}
                placeholder="Estimation immobilière précise à [ville]. Visite sur place avec outils professionnels..."
                data-testid="meta-description-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Apparait sous le titre dans Google. Optimal: 150-160 caractères
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Open Graph (Social Media) */}
      <Card className="p-6 space-y-6">
        <div>
          <h3 className="font-semibold mb-1">Open Graph (Réseaux sociaux)</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Contrôle l'apparence lors du partage sur Facebook, LinkedIn, etc.
          </p>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="og_title">OG Title</Label>
              <Input
                id="og_title"
                value={seo.og_title}
                onChange={(e) => updateField('og_title', e.target.value)}
                placeholder="Titre pour réseaux sociaux (optionnel)"
                data-testid="og-title-input"
              />
            </div>

            <div>
              <Label htmlFor="og_description">OG Description</Label>
              <Textarea
                id="og_description"
                value={seo.og_description}
                onChange={(e) => updateField('og_description', e.target.value)}
                rows={2}
                placeholder="Description pour réseaux sociaux (optionnel)"
                data-testid="og-description-input"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Canonical */}
      <Card className="p-6">
        <div>
          <Label htmlFor="canonical_url">URL Canonique</Label>
          <Input
            id="canonical_url"
            value={seo.canonical_url}
            onChange={(e) => updateField('canonical_url', e.target.value)}
            placeholder="https://votre-site.ch/estimation-geneve"
            data-testid="canonical-url-input"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Optionnel. Utile si vous avez un domaine personnalisé.
          </p>
        </div>
      </Card>

      {/* Preview */}
      <Card className="p-6 bg-muted/30">
        <h3 className="font-semibold mb-3">Aperçu SERP (Google)</h3>
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-sm text-blue-700 mb-1">
            {seo.meta_title || `Estimation Immobilière ${agent.city} | Expert Local`}
          </div>
          <div className="text-xs text-green-700 mb-2">
            https://estimation-reelle.ch/a/{agent.slug}
          </div>
          <div className="text-sm text-gray-700">
            {seo.meta_description || `Estimation immobilière précise à ${agent.city}. Visite sur place avec outils professionnels. Contact: ${agent.first_name} ${agent.last_name}`}
          </div>
        </div>
      </Card>

      <Button onClick={handleSave} disabled={loading} data-testid="save-seo-button">
        {loading ? 'Enregistrement...' : 'Enregistrer les paramètres SEO'}
      </Button>
    </div>
  );
};

export default SEOEditor;
